import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useCallback,
} from "react";
import { jumpTo } from "../../utils";

// 单个头像组件（支持图片加载失败占位）
const AvatarItem = ({ src, alt, userId, isNew, onAnimationEnd }) => {
  const [imgError, setImgError] = useState(false);
  return (
    <div
      className={`avatar-cell ${isNew ? "avatar-new" : ""}`}
      onAnimationEnd={isNew ? onAnimationEnd : undefined}
    >
      {!imgError ? (
        <img
          onClick={() => {
            jumpTo('storm://app/userzone?userID=' + userId)
          }}
          src={src}
          alt={alt}
          className="avatar-image"
          onError={() => setImgError(true)}
          loading="lazy"
        />
      ) : (
        <div className="avatar-placeholder">
          <span className="placeholder-icon" aria-label="点赞" role="img">🖼️</span>
        </div>
      )}
    </div>
  );
};

// 头像墙组件（6x4网格）
// 接收 avatars 数组（元素需包含 id, url 字段），并暴露 addAvatar 方法给外部
const AvatarWall = forwardRef(({ avatars = [], onAddOverflow }, ref) => {
  // 内部维护一个带 isNew 标记的头像列表，以便在添加时控制动画
  const [items, setItems] = useState(() =>
    avatars.map((item, idx) => ({
      ...item,
      isNew: false,
      _key: item.id || idx,
    })),
  );

  // 当外部 avatars 变化时（比如初始数据或外部重置），同步内部状态（保留 isNew 标记只用于动画）
  useEffect(() => {
    setItems((prev) => {
      // 如果外部数据长度或内容变化，重新构建，但保留原有头像的 isNew 为 false（新添加的会由 addAvatar 单独处理）
      const newItems = avatars.map((item, idx) => {
        // const existing = prev.find((p) => p.id === item.id);
        return { ...item, isNew: false, _key: item.id || idx };
      });
      return newItems;
    });
  }, [avatars]);

  // 暴露给外部的方法：添加新头像
  const addAvatar = useCallback(
    ({ avatar, userId }, customId) => {
      const newId = customId || Date.now();
      const newAvatar = {
        id: newId,
        userId: userId,
        url: avatar,
        isNew: true,
        user: {
          userId,
          // userName: '新用户',
          avatar,
        },
        _key: newId,
      };

      setItems((prev) => {
        
       prev = prev.filter(item => item.user.userId !== userId);

        let newList = [newAvatar, ...prev];
        const MAX_COUNT = 24; // 6x4
        if (newList.length > MAX_COUNT) {
          newList = newList.slice(0, MAX_COUNT);
          // 可选回调通知外部移除了末尾项
          // if(onAddOverflow) {
          //   onAddOverflow[prev[prev.length - 1]];
          // }
          // onAddOverflow && onAddOverflow[prev[prev.length - 1]];
        }
        return newList;
      });

      // 动画持续 400ms 后移除 isNew 标记
      const timer = setTimeout(() => {
        setItems((current) =>
          current.map((item) =>
            item.id === newId ? { ...item, isNew: false } : item,
          ),
        );
      }, 400);
      return () => clearTimeout(timer);
    },
    [onAddOverflow],
  );

  useImperativeHandle(ref, () => ({
    addAvatar,
  }));

  return (
    <div className="avatar-wall">
      {/* {JSON.stringify(items)} */}
      <div className="avatar-grid">
        {items.slice(0, 24).map((item) => {
          console.log(item);

          return (
            <AvatarItem
              key={item._key}
              src={item.user.avatar}
              alt={`avatar-${item.id}`}
              userId={item.user.userId}
              isNew={item.isNew}
              onAnimationEnd={() => {
                // 动画结束后额外保证清除标记（双重保障）
                setItems((current) =>
                  current.map((i) =>
                    i.id === item.id && i.isNew ? { ...i, isNew: false } : i,
                  ),
                );
              }}
            />
          );
        })}
      </div>

      <style>{`
        .avatar-wall {
          max-width: 1200px;
          margin: 0 auto;
          padding: 1rem;
        }
        .avatar-grid {
          display: grid;
          grid-template-columns: repeat(6, minmax(0, 1fr));
          gap: 1.25rem;
          justify-items: center;
          align-items: center;
        }
        .avatar-cell {
          width: 100%;
          aspect-ratio: 1 / 1;
          border-radius: 50%;
          overflow: hidden;
          background: #fff;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
          transition: transform 3s ease, box-shadow 3s;
          cursor: pointer;
        }
        .avatar-cell:hover {
          transform: scale(1.03);
          box-shadow: 0 18px 30px -8px rgba(0, 0, 0, 0.2);
        }
        /* 新头像动画 */
        .avatar-new {
          // animation: popIn 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) forwards;
          box-shadow: 0 0 0 3px #a2060d, 0 12px 24px rgba(0,0,0,0.2);
        }
        @keyframes popIn {
          0% {
            opacity: 0;
            transform: scale(0.6);
          }
          70% {
            transform: scale(1.05);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .avatar-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .avatar-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(145deg, #cbd5e1, #94a3b8);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .placeholder-icon {
          font-size: calc(min(6vw, 2rem));
          opacity: 0.8;
        }
        @media (max-width: 768px) {
          .avatar-grid {
            gap: 0.85rem;
          }
        }
        @media (max-width: 540px) {
          .avatar-grid {
            gap: 0.6rem;
          }
          .placeholder-icon {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
});


export default AvatarWall;



// WEBPACK FOOTER //
// ./src/views/activity_210/AvatarWall.js